/**
 * CMS feature flags as build-time kill-switches (DIT-264).
 *
 * Belgium-specific marketing surfaces (the `/law-2027` page + its links, the
 * CheckIn@Work / CIAO tiles) render by default for the belgium market. A CMS
 * feature flag can KILL one of those surfaces without a code change: the admin
 * toggles it off, the API fires a landing rebuild, and the next prerendered build
 * omits the surface. Because the site is fully prerendered, a flag is necessarily
 * baked at build time — the kill-switch latency is one deploy cycle.
 *
 * SEMANTICS — subtractive, not additive. A surface is live UNLESS its flag is
 * explicitly `false` for the country. Absent flag data (offline/CI build with no
 * CMS, or an un-seeded country) therefore keeps every market-default surface, so a
 * transient CMS hiccup can never blank out the Belgian content. Only a deliberate
 * admin toggle to `false` removes a surface. Flags do NOT turn surfaces ON for a
 * market where the market gate already hides them.
 *
 * Pure data (NFR10): imported by both the Vite render bundle AND the Node-side
 * prerender matrix (`app/ssg/included-routes.ts`), so this module — like
 * `content/pricing.ts` — must stay free of React / browser globals.
 */
import { CMS_FLAGS } from './_generated/cms-flags';
import type { Market } from '../i18n/locales';

/**
 * Market → CountrySite code for flag lookup. The flag-controlled surfaces are
 * belgium-only, so the only meaningful mapping is `belgium → be`; everything else
 * (incl. the `lt` locale, whose market is `international`) resolves to `int`, which
 * carries no kill-switches today. A future non-belgium flag would need the locale
 * code, not just the market — extend then.
 */
const countryForMarket = (market: Market): string => (market === 'belgium' ? 'be' : 'int');

/** True unless the flag is explicitly disabled for `country` (kill-switch). */
export function featureLive(country: string, slug: string): boolean {
  return CMS_FLAGS[country]?.[slug] !== false;
}

/** `featureLive` keyed by the runtime market (what `useLocale()` exposes). */
export function featureLiveForMarket(market: Market, slug: string): boolean {
  return featureLive(countryForMarket(market), slug);
}
